import {
  differenceInHours,
  isAfter,
  isBefore,
  endOfDay,
  eachHourOfInterval,
  eachMinuteOfInterval,
  startOfHour,
  addHours,
} from "date-fns"
import { GQL } from "platform-common/index"
import { format, formatInTimeZone } from "date-fns-tz"

import { EMPTY_VACANCY_COLOR, VACANCY_COLORS } from "./TimelineStats.const"
import { TimelineType } from "./TimelineStats.types"
import { WorkBlockType } from "platform-common/types/userActivity"
import { getWorkBlockStartAndEnd, getWorkBlocks } from "platform-common/util/userActivity"
import { convertTargetTimeToClientTime } from "platform-common/util/timeZones"
import { EventFilterOption } from "../IndividualEventFilters/IndividualEventFilters"

const pad = (value: number) => {
  return String(value).padStart(2, "0")
}
export const EVENT_NAME_MAPPING = {
  CANDIDATE_PAGE_OPENED: "Candidate Page Opened",
  ATS_PAGE_OPENED: "ATS",
  SEND_EMAIL_CLICKED: "Email Sequence Sent",
  MAIL_INBOX: "Email Inbox Opened",
  LI_CONNECTION_REQUEST_SENT: "Automated New Connections",
  WORK_OTHER_PAGE_OPENED: "Other Work Activity",
  CALENDAR_PAGE_OPENED: "Calendar",
  COMMUNICATION_PAGE_OPENED: "Messaging App",
  PRODUCTIVITY_PAGE_OPENED: "Productivity App",
  FILE_OPENED: "Files Opened",
} as Record<GQL.UserActivityEvent, string>

const SPECIAL_EVENT_COLORS = {
  CANDIDATE_PAGE_OPENED: "#3d00be",
  ATS_PAGE_OPENED: "#2c9104",
  SEND_EMAIL_CLICKED: "#ff9400",
  MAIL_INBOX: "#ff3333",
  LI_CONNECTION_REQUEST_SENT: "#1b9eb8",
  CALENDAR_PAGE_OPENED: "#9b4ae8",
  COMMUNICATION_PAGE_OPENED: "#1976d2",
  PRODUCTIVITY_PAGE_OPENED: "#ad5300",
  FILE_OPENED: "#e747a4",
  WORK_OTHER_PAGE_OPENED: "black",
} as Partial<Record<GQL.UserActivityEvent, string>>

export const getClientSideEventName = (event: GQL.UserActivityEvent) => {
  return EVENT_NAME_MAPPING[event] || event
}

export const getEventColor = (event: GQL.UserActivityEvent) => {
  return SPECIAL_EVENT_COLORS[event] || event
}

/**
 * @returns `left` and `width` CSS props of a work block inside the timeline calculated based on
 * work block start time and duration.
 */
export const getWorkBlockStartAndWidth = (workBlock: WorkBlockType, timelineStart: Date, timelineEnd: Date) => {
  const { start, end } = getWorkBlockStartAndEnd(workBlock)
  if (!start || !end) return { left: "0%", width: "1px" }
  const total = +timelineEnd - +timelineStart
  const value = +start - +timelineStart
  const volume = +end - +start

  const progress = (value / total) * 100
  const width = (volume / total) * 100

  return {
    left: `${progress}%`,
    width: `max(${width}%, 1px)`,
  }
}

/**
 * @returns value to use as `left` CSS prop to position an things inside a timeline.
 */
export const getDatePosition = (date: Date | undefined, start: Date | undefined, end: Date | undefined) => {
  if (!date || !start || !end) return "0%"
  const total = +end - +start
  const value = +date - +start

  const progress = (value / total) * 100 || 0

  return progress === 100 ? `calc(100% - 1px)` : `${progress}%`
}

/**
 * @returns human readable presentation of miliseconds.
 */
export const formatDuration = (hours: number, fallback?: string) => {
  if (fallback && !hours) return fallback

  const seconds = Math.floor(hours * 3600) % 60
  const minutes = Math.floor(hours * 60) % 60
  const formattedHours = Math.floor(hours)

  return pad(formattedHours) + ":" + pad(minutes) + ":" + pad(seconds)
}

/**
 * @returns human readable presentation of date.
 */
export const formatDate = (date: Date, timeZone?: string) => {
  const formatting = "EEE, d MMM yyyy" // Mon, 1 Jan 2020
  const formatted = timeZone ? formatInTimeZone(date, timeZone, formatting) : format(date, formatting)

  return formatted
}
export const shortDate = (date: Date, timeZone?: string) => {
  const formatting = "d MMM yyyy" // Mon, 1 Jan 2020
  const formatted = timeZone ? formatInTimeZone(date, timeZone, formatting) : format(date, formatting)

  return formatted
}

/**
 * @returns human readable presentation of time.
 */
export const formatTime = (date: Date, withSeconds?: boolean) => {
  const formatting = `HH:mm${withSeconds ? ":ss" : ""}` // 10:00:00 or 10:00
  const formatted = format(date, formatting)

  return formatted
}

/**
 * @returns boolean indicating whether start and end dates are correctly ordered.
 */
export const isDateRangeValid = (start: Date, end: Date) => {
  return +end - +start > 0
}

type Vacancy = {
  name?: string | null
  totalTime: number
  events: Array<{
    name: GQL.UserActivityEvent
    color: string
    count: number
  }>
}

/**
 * @returns events with assigned colors, default black or color defined for selected events
 */
export const getColoredEvents = (events: GQL.Event[]) => {
  return events.filter(Boolean).map((e) => ({
    ...e,
    name: e.name,
    recordedAt: new Date(e.recordedAt).toISOString(),
    color: e.domainCategory?.color || SPECIAL_EVENT_COLORS[e.name] || "black",
  }))
}

/**
 * Assign a color to each vacancy in event list, it helps to visually distinguish work blocks
 * that assiciate with different vacancies
 */
const getVacancyColors = (events: GQL.Event[]) => {
  const vacancyColors = new Map<GQL.Maybe<string>, string>([[null, EMPTY_VACANCY_COLOR]])

  events.forEach((e) => {
    let vacancyColor = vacancyColors.get(e.vacancy)

    if (!vacancyColor) {
      vacancyColor = VACANCY_COLORS[(vacancyColors.size - 1) % VACANCY_COLORS.length] || "#F6E4FF"
      vacancyColors.set(e.vacancy, vacancyColor)
    }
  })

  return vacancyColors
}

export const getUserTimelines = (
  timelineStart: Date,
  timelineEnd: Date,
  userEvents: {
    userId: string
    userName: string
    userTimeZone?: string | null
    events: GQL.Event[]
    summary: GQL.UserActivitySummary
  }[]
) => {
  const vacancyColors = getVacancyColors(userEvents.flatMap((e) => e.events))

  const timelines = userEvents.map((user) => {
    const events = [...user.events].filter(Boolean).sort((a, b) => (a.recordedAt < b.recordedAt ? -1 : 1))

    // shift events toward client time zone to get client days of the month
    const convertedToClientTime = getColoredEvents(
      events.filter(Boolean).map((e) => ({
        ...e,
        recordedAt: convertTargetTimeToClientTime({
          date: new Date(e.recordedAt),
          targetTimeZone: user.userTimeZone,
          clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }).toISOString(),
      }))
    )

    const timeline: TimelineType = {
      userId: user.userId,
      userName: user.userName,
      start: timelineStart,
      timeZone: user.userTimeZone,
      end: timelineEnd,
      workBlocks: getWorkBlocks(
        convertedToClientTime.filter(
          (e) => e && isAfter(new Date(e.recordedAt), timelineStart) && isBefore(new Date(e.recordedAt), timelineEnd)
        ),
        vacancyColors
      ),
      summary: user.summary,
    }

    return stripTimeline(timeline)
  })

  return timelines
}

/**
 * Round timeline `start` and `end` towards start and end of hour respectively
 */
export const stripTimeline = (timeline: TimelineType) => {
  const { workBlocks } = timeline

  if (!workBlocks.filter(Boolean).length) return timeline

  const firstEvent = workBlocks[0]?.events[0]
  if (!firstEvent) return timeline
  const lastWb = workBlocks[workBlocks.length - 1]
  const lastEvent = lastWb?.events[lastWb?.events.length - 1]
  if (!lastEvent) return timeline

  return {
    ...timeline,
    start: startOfHour(new Date(firstEvent.recordedAt)),
    end: new Date(
      Math.min(+startOfHour(addHours(new Date(lastEvent.recordedAt), 1)), +endOfDay(new Date(lastEvent.recordedAt)))
    ),
  }
}

/**
 * Calculate time labels to render between start and end of the timeline,
 * the longer the timeline - the bigger the step.
 */
export const getTimeMarks = (start_: Date | undefined, end: Date | undefined) => {
  if (!start_ || !end) return []
  const start = startOfHour(start_)

  let difference = differenceInHours(start, end)
  let timeMarks: Date[]

  difference = difference < 0 ? difference * -1 : difference

  if (difference > 12) {
    timeMarks = eachHourOfInterval({ start, end }, { step: 2 })
  } else if (difference > 4) {
    timeMarks = eachHourOfInterval({ start, end }, { step: 1 })
  } else if (difference > 1) {
    timeMarks = eachMinuteOfInterval({ start, end }, { step: 30 })
  } else {
    timeMarks = eachMinuteOfInterval({ start, end }, { step: 10 })
  }

  return timeMarks.filter((t) => isAfter(t, start_) && isBefore(t, end))
}

export const isEmptyStats = (
  summary: GQL.UserActivitySummary,
  hasOtherWorkEvents: boolean,
  skippedIndividualEvents?: EventFilterOption[]
) => {
  const skippedEventIdentifiers = skippedIndividualEvents?.map((e) => e.value) || []

  return (
    (!summary.profilesViewed || skippedEventIdentifiers.includes("CANDIDATE_PAGE_OPENED")) &&
    (!summary.liConnectionRequestSent || skippedEventIdentifiers.includes("LI_CONNECTION_REQUEST_SENT")) &&
    (!summary.mailInboxOpened || skippedEventIdentifiers.includes("MAIL_INBOX")) &&
    (!summary.emailsSent || skippedEventIdentifiers.includes("SEND_EMAIL_CLICKED")) &&
    (!summary.atsPageOpened || skippedEventIdentifiers.includes("ATS_PAGE_OPENED")) &&
    (!summary.calendarPageOpened || skippedEventIdentifiers.includes("CALENDAR_PAGE_OPENED")) &&
    (!summary.communicationPageOpened || skippedEventIdentifiers.includes("COMMUNICATION_PAGE_OPENED")) &&
    (!summary.productivityPageOpened || skippedEventIdentifiers.includes("PRODUCTIVITY_PAGE_OPENED")) &&
    (!summary.fileOpened || skippedEventIdentifiers.includes("FILE_OPENED")) &&
    (!hasOtherWorkEvents || skippedEventIdentifiers.includes("WORK_OTHER_PAGE_OPENED")) &&
    !summary.domainCategorySummary?.filter((c) => !skippedEventIdentifiers.includes(c.id))?.length
  )
}
